/**
 * Sources - Files
 * Manage file sources for AI training
 */

import React, { useRef, useCallback, useState, useEffect } from 'react';
import UsageChip from '@/components/common/UsageChip';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Alert,
  CircularProgress,
  Chip,
  Paper,
  TablePagination,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  CloudUpload as UploadIcon,
  Description as FileIcon,
  Refresh as RefreshIcon,
  Visibility as ViewIcon,
  AttachFile as AttachFileIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { useFileSources, FileSourceItem } from '@hooks/useFileSources';
import ConfirmDialog from '@/components/common/ConfirmDialog';
import FileViewer from '@/components/common/FileViewer';
import AttachFilesDialog from '@/components/admin/AttachFilesDialog';
import adminApi from '@/services/admin.api';

interface AttachedAgent {
  _id: string;
  name: string;
  topics?: string[];
}

const FilesSource: React.FC = () => {
  const {
    files,
    loading,
    uploading,
    error,
    success,
    page,
    setPage,
    total,
    PAGE_SIZE,
    maxUploadSizeMb,
    uploadFiles,
    deleteFile,
    getFileDownloadUrl,
    reload,
    clearError,
  } = useFileSources();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [viewingFile, setViewingFile] = useState<{
    id: string;
    name: string;
    mimeType: string;
  } | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<{
    open: boolean;
    fileId: string | null;
    fileName: string;
  }>({
    open: false,
    fileId: null,
    fileName: '',
  });
  const [attachDialogOpen, setAttachDialogOpen] = useState(false);
  const [selectedFileForAttach, setSelectedFileForAttach] = useState<FileSourceItem | null>(null);
  const [fileAgents, setFileAgents] = useState<Record<string, AttachedAgent[]>>({});
  const [loadingAgents, setLoadingAgents] = useState<Record<string, boolean>>({});
  const [detachConfirm, setDetachConfirm] = useState<{
    open: boolean;
    fileId: string | null;
    agentId: string | null;
    fileName: string;
    agentName: string;
  }>({
    open: false,
    fileId: null,
    agentId: null,
    fileName: '',
    agentName: '',
  });

  // Load agents for each file
  useEffect(() => {
    const loadAgentsForFiles = async () => {
      for (const file of files) {
        if (!fileAgents[file._id] && !loadingAgents[file._id]) {
          setLoadingAgents((prev) => ({ ...prev, [file._id]: true }));
          try {
            const agents = await adminApi.getFileAgents(file._id);
            setFileAgents((prev) => ({ ...prev, [file._id]: agents }));
          } catch (err) {
            console.error(`Failed to load agents for file ${file._id}:`, err);
            setFileAgents((prev) => ({ ...prev, [file._id]: [] }));
          } finally {
            setLoadingAgents((prev) => ({ ...prev, [file._id]: false }));
          }
        }
      }
    };

    if (files.length > 0) {
      loadAgentsForFiles();
    }
  }, [files, fileAgents, loadingAgents]);

  const handleFileSelect = useCallback(
    (fileList: FileList | null) => {
      if (!fileList || fileList.length === 0) return;
      const filesArray = Array.from(fileList);
      uploadFiles(filesArray);
      // Reset file input to allow selecting the same file again
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    },
    [uploadFiles]
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);
      handleFileSelect(e.dataTransfer.files);
    },
    [handleFileSelect]
  );

  const handleViewFile = useCallback((file: FileSourceItem) => {
    setViewingFile({
      id: file._id,
      name: file.originalName || file.name,
      mimeType: file.mimeType,
    });
  }, []);

  const handleCloseViewer = useCallback(() => {
    setViewingFile(null);
  }, []);

  const handleDeleteClick = useCallback((fileId: string, fileName: string) => {
    setDeleteConfirm({
      open: true,
      fileId,
      fileName,
    });
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (deleteConfirm.fileId) {
      try {
        await deleteFile(deleteConfirm.fileId);
        setDeleteConfirm({
          open: false,
          fileId: null,
          fileName: '',
        });
        // Remove from fileAgents state
        setFileAgents((prev) => {
          const updated = { ...prev };
          delete updated[deleteConfirm.fileId!];
          return updated;
        });
      } catch (err) {
        // Error is handled by the hook
        console.error('Failed to delete file:', err);
      }
    }
  }, [deleteConfirm.fileId, deleteFile]);

  const handleCancelDelete = useCallback(() => {
    setDeleteConfirm({
      open: false,
      fileId: null,
      fileName: '',
    });
  }, []);

  const handleAttachToAgents = useCallback((file: FileSourceItem) => {
    setSelectedFileForAttach(file);
    setAttachDialogOpen(true);
  }, []);

  const handleAttachSuccess = useCallback(async () => {
    reload(); // Refresh files list
    // Reload agents for the attached file
    if (selectedFileForAttach) {
      try {
        const agents = await adminApi.getFileAgents(selectedFileForAttach._id);
        setFileAgents((prev) => ({ ...prev, [selectedFileForAttach._id]: agents }));
      } catch (err) {
        console.error('Failed to reload agents:', err);
      }
    }
  }, [reload, selectedFileForAttach]);

  const handleCloseAttachDialog = useCallback(() => {
    setAttachDialogOpen(false);
    setSelectedFileForAttach(null);
  }, []);

  const handleDetachFromAgent = useCallback(
    (fileId: string, agentId: string, fileName: string, agentName: string) => {
      setDetachConfirm({
        open: true,
        fileId,
        agentId,
        fileName,
        agentName,
      });
    },
    []
  );

  const handleConfirmDetach = useCallback(async () => {
    if (detachConfirm.fileId && detachConfirm.agentId) {
      try {
        await adminApi.detachFileFromAgent(detachConfirm.agentId, detachConfirm.fileId, false);
        // Reload agents for this file
        const agents = await adminApi.getFileAgents(detachConfirm.fileId);
        setFileAgents((prev) => ({ ...prev, [detachConfirm.fileId!]: agents }));
        setDetachConfirm({
          open: false,
          fileId: null,
          agentId: null,
          fileName: '',
          agentName: '',
        });
      } catch (err: any) {
        console.error('Failed to detach file:', err);
      }
    }
  }, [detachConfirm]);

  const handleCancelDetach = useCallback(() => {
    setDetachConfirm({
      open: false,
      fileId: null,
      agentId: null,
      fileName: '',
      agentName: '',
    });
  }, []);

  const formatFileSize = useCallback((bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  }, []);

  const getFileTypeIcon = useCallback((mimeType: string): string => {
    if (mimeType === 'application/pdf') return 'PDF';
    if (mimeType === 'application/msword') return 'DOC';
    if (mimeType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document')
      return 'DOCX';
    if (mimeType === 'text/plain') return 'TXT';
    return 'FILE';
  }, []);

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={2}>
        <Typography variant="h4" fontWeight="bold">
          File Sources
        </Typography>
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
          <UsageChip resource="storage" label="Storage" format="bytes" />
          <Button startIcon={<RefreshIcon />} onClick={reload} disabled={loading}>
            Refresh
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={clearError}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => {}}>
          {success}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Upload Area */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Upload Files
              </Typography>
              <Typography variant="body2" color="text.secondary" mb={2}>
                Supported file types: PDF, DOC, DOCX, TXT only
              </Typography>

              <Paper
                variant="outlined"
                sx={{
                  p: 4,
                  textAlign: 'center',
                  border: isDragging ? '2px dashed' : '1px dashed',
                  borderColor: isDragging ? 'primary.main' : 'divider',
                  backgroundColor: isDragging ? 'action.hover' : 'background.paper',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                }}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
              >
                <UploadIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" gutterBottom>
                  Drag & drop files here, or click to select files
                </Typography>
                <Typography variant="body2" color="text.secondary" mb={2}>
                  Maximum file size: {maxUploadSizeMb != null ? `${maxUploadSizeMb}MB` : '…'}
                </Typography>
                <Button variant="contained" component="span" disabled={uploading}>
                  {uploading ? 'Uploading...' : 'Select Files'}
                </Button>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept=".pdf,.doc,.docx,.txt,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain"
                  style={{ display: 'none' }}
                  onChange={(e) => handleFileSelect(e.target.files)}
                />
              </Paper>
            </CardContent>
          </Card>
        </Grid>

        {/* Files List */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Uploaded Files ({total})
              </Typography>

              {loading ? (
                <Box display="flex" justifyContent="center" p={3}>
                  <CircularProgress />
                </Box>
              ) : files.length === 0 ? (
                <Typography variant="body2" color="text.secondary" textAlign="center" p={3}>
                  No files uploaded yet. Upload files using the area above.
                </Typography>
              ) : (
                <List>
                  {files.map((file) => {
                    const agents = fileAgents[file._id] || [];
                    const isLoadingAgents = loadingAgents[file._id];

                    return (
                      <ListItem
                        key={file._id}
                        sx={{
                          borderBottom: '1px solid',
                          borderColor: 'divider',
                          '&:hover': {
                            backgroundColor: 'action.hover',
                          },
                          cursor: 'pointer',
                        }}
                        onClick={() => handleViewFile(file)}
                      >
                        <FileIcon sx={{ mr: 2, color: 'text.secondary' }} />
                        <ListItemText
                          primary={
                            <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                              <Typography variant="body1">
                                {file.originalName || file.name}
                              </Typography>
                              <Chip
                                label={getFileTypeIcon(file.mimeType)}
                                size="small"
                                variant="outlined"
                              />
                              <Typography variant="caption" color="text.secondary">
                                {formatFileSize(file.size)}
                              </Typography>
                            </Box>
                          }
                          secondary={
                            <Box>
                              <Typography variant="caption" display="block">
                                Uploaded by {file.createdByName} •{' '}
                                {new Date(file.createdAt).toLocaleDateString()}
                              </Typography>
                              {file.uniqueName && (
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  display="block"
                                >
                                  Stored as: {file.uniqueName}
                                </Typography>
                              )}
                              {isLoadingAgents ? (
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  display="block"
                                  sx={{ mt: 0.5 }}
                                >
                                  Loading agents...
                                </Typography>
                              ) : agents.length > 0 ? (
                                <Box
                                  display="flex"
                                  alignItems="center"
                                  gap={0.5}
                                  flexWrap="wrap"
                                  sx={{ mt: 0.5 }}
                                >
                                  <Typography variant="caption" color="text.secondary">
                                    Attached to:
                                  </Typography>
                                  {agents.map((agent) => (
                                    <Chip
                                      key={agent._id}
                                      label={agent.name}
                                      size="small"
                                      onDelete={(e) => {
                                        e.stopPropagation();
                                        handleDetachFromAgent(
                                          file._id,
                                          agent._id,
                                          file.originalName || file.name,
                                          agent.name
                                        );
                                      }}
                                      deleteIcon={<CloseIcon />}
                                      sx={{ height: 20, fontSize: '0.7rem' }}
                                    />
                                  ))}
                                </Box>
                              ) : (
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  display="block"
                                  sx={{ mt: 0.5 }}
                                >
                                  Not attached to any agents
                                </Typography>
                              )}
                            </Box>
                          }
                        />
                        <ListItemSecondaryAction>
                          <IconButton
                            edge="end"
                            color="primary"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleViewFile(file);
                            }}
                            sx={{ mr: 1 }}
                            title="View file"
                          >
                            <ViewIcon />
                          </IconButton>
                          <IconButton
                            edge="end"
                            color="primary"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleAttachToAgents(file);
                            }}
                            sx={{ mr: 1 }}
                            title="Attach to agents"
                          >
                            <AttachFileIcon />
                          </IconButton>
                          <IconButton
                            edge="end"
                            color="error"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteClick(file._id, file.originalName || file.name);
                            }}
                            title="Delete file"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </ListItemSecondaryAction>
                      </ListItem>
                    );
                  })}
                </List>
              )}

              {total > 0 && (
                <TablePagination
                  component="div"
                  count={total}
                  page={page}
                  onPageChange={(_e, p) => setPage(p)}
                  rowsPerPage={PAGE_SIZE}
                  rowsPerPageOptions={[PAGE_SIZE]}
                />
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* File Viewer Dialog */}
      {viewingFile && (
        <FileViewer
          open={!!viewingFile}
          fileId={viewingFile.id}
          fileName={viewingFile.name}
          mimeType={viewingFile.mimeType}
          onClose={handleCloseViewer}
          getDownloadUrl={getFileDownloadUrl}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={deleteConfirm.open}
        title="Confirm Delete"
        content="Are you sure you want to delete this file?"
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
        loading={false}
      />

      {/* Detach Confirmation Dialog */}
      <ConfirmDialog
        open={detachConfirm.open}
        title="Detach File from Agent"
        content={`Are you sure you want to detach "${detachConfirm.fileName}" from "${detachConfirm.agentName}"?`}
        confirmText="Detach"
        cancelText="Cancel"
        onConfirm={handleConfirmDetach}
        onCancel={handleCancelDetach}
        loading={false}
      />

      {/* Attach Files Dialog */}
      <AttachFilesDialog
        open={attachDialogOpen}
        fileIds={selectedFileForAttach ? [selectedFileForAttach._id] : []}
        fileName={selectedFileForAttach?.name || ''}
        onClose={handleCloseAttachDialog}
        onSuccess={handleAttachSuccess}
      />
    </Box>
  );
};

export default FilesSource;
